import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { UserPlus, Sparkles, Heart } from "lucide-react";

export default function PatientRegister() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: "", password: "", full_name: "",
    phone: "", date_of_birth: "", codice_fiscale: "",
    allergies: "", diseases: "", medications: "",
  });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (step === 1) { setStep(2); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/patient-auth/register-global", form);
      localStorage.setItem("patient_token", data.token);
      localStorage.setItem("patient_data", JSON.stringify(data.patient));
      toast.success(t("patientRegister.welcome"));
      navigate("/mi-portal");
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid="patient-register-page">
      <header className="border-b border-stone-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/bienvenido" className="flex items-baseline gap-2">
            <span className="font-heading italic text-2xl text-[#B07D4F]">A</span>
            <span className="font-heading text-2xl font-light">Ambra</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-stone-400">patient matter</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/portal" data-testid="patient-register-to-login"
              className="text-sm text-stone-600 hover:text-stone-900 underline underline-offset-4">
              {t("patientRegister.haveAccount")}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-4xl w-full grid lg:grid-cols-2 gap-14 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4A373]/15 text-[#B07D4F] text-[11px] uppercase tracking-widest">
              <Heart className="w-3 h-3" /> {t("patientRegister.eyebrow")}
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-light tracking-tight">
              {t("patientRegister.title")}
            </h1>
            <p className="text-stone-600 leading-relaxed">{t("patientRegister.subtitle")}</p>
            <div className="border-l-2 border-[#D4A373] pl-6 space-y-3 text-stone-600 text-sm">
              <div className="flex items-start gap-3"><Sparkles className="w-3.5 h-3.5 mt-1 text-[#D4A373]" /><p>{t("patientRegister.b1")}</p></div>
              <div className="flex items-start gap-3"><Sparkles className="w-3.5 h-3.5 mt-1 text-[#D4A373]" /><p>{t("patientRegister.b2")}</p></div>
              <div className="flex items-start gap-3"><Sparkles className="w-3.5 h-3.5 mt-1 text-[#D4A373]" /><p>{t("patientRegister.b3")}</p></div>
              <div className="flex items-start gap-3"><Sparkles className="w-3.5 h-3.5 mt-1 text-[#D4A373]" /><p><strong>{t("patientRegister.free")}</strong></p></div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm">
            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-stone-500">
              <UserPlus className="w-3.5 h-3.5" /> {t("patientRegister.step")} {step} / 2
            </div>
            <h3 className="font-heading text-2xl font-light mb-6">
              {step === 1 ? t("patientRegister.step1Title") : t("patientRegister.step2Title")}
            </h3>
            <form onSubmit={submit} className="space-y-4" data-testid="patient-register-form">
              {step === 1 && (
                <>
                  <Field label={t("patientRegister.fullName")} testId="pr-name" required
                    value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
                  <Field label="Email" type="email" testId="pr-email" required
                    value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                  <Field label="Password" type="password" testId="pr-password" required minLength={6}
                    value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
                </>
              )}
              {step === 2 && (
                <>
                  <p className="text-xs text-stone-500 mb-2">{t("patientRegister.step2Desc")}</p>
                  <Field label={t("booking.phone")} required testId="pr-phone" placeholder="+39 …"
                    value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("booking.dob")} type="date" testId="pr-dob"
                      value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} />
                    <Field label={t("booking.cf")} testId="pr-cf"
                      value={form.codice_fiscale} onChange={(v) => setForm({ ...form, codice_fiscale: v })} />
                  </div>
                  <TextArea label={t("booking.allergies")} rows={2} testId="pr-allergies"
                    value={form.allergies} onChange={(v) => setForm({ ...form, allergies: v })} />
                  <TextArea label={t("booking.diseases")} rows={2} testId="pr-diseases"
                    value={form.diseases} onChange={(v) => setForm({ ...form, diseases: v })} />
                  <TextArea label={t("booking.medications")} rows={2} testId="pr-meds"
                    value={form.medications} onChange={(v) => setForm({ ...form, medications: v })} />
                </>
              )}
              <div className="flex gap-2 pt-2">
                {step === 2 && (
                  <button type="button" onClick={() => setStep(1)}
                    className="px-4 py-3 rounded-full border border-stone-200 text-stone-600 hover:bg-stone-50 text-sm">
                    ← {t("common.back")}
                  </button>
                )}
                <button type="submit" disabled={loading} data-testid="pr-submit"
                  className="flex-1 py-3 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors disabled:opacity-60 font-medium">
                  {loading ? "…" : (step === 1 ? t("landing.next") : t("patientRegister.submit"))}
                </button>
              </div>
            </form>
            <p className="text-xs text-stone-400 mt-6 text-center">{t("patientRegister.gdpr")}</p>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, minLength, placeholder, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1.5 block">{label}</label>
      <input type={type} value={value} required={required} minLength={minLength} placeholder={placeholder}
        data-testid={testId} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
    </div>
  );
}

function TextArea({ label, value, onChange, rows, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1.5 block">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
        spellCheck autoCorrect="on" autoCapitalize="sentences" data-testid={testId}
        className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
    </div>
  );
}
